<template>
  <section
    id="profileHealthCheck"
    v-if="employee.latestHealthCheckResult || nextPendingHealthCheck"
  >
    <v-row align="center" class="mt-3" style="max-height: 56px">
      <v-col>
        <span class="title text--secondary text-uppercase">
          {{ $t("healthCheck.title") }}
        </span>
      </v-col>
      <v-col class="text-end">
        <v-btn
          v-if="
            employee.latestHealthCheckResult && role('safetyRepresentative')
          "
          small
          class="primary accent-4 white--text mr-2"
          @click="showEditResultsDialog = true"
        >
          <v-icon small left>edit</v-icon>
          {{ $t("dialog.edit") }}
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="[employee.latestHealthCheckResult || {}]"
      class="safety-elevation-3 table-striped"
      hide-default-footer
    >
      <template v-slot:[`item.healthCheckOccurredAt`]="{ item }">
        <span>
          {{ item.healthCheckOccurredAt | date }}
        </span>
      </template>

      <template v-slot:[`item.nextHealthCheckDateTime`]="{ item }">
        <span>
          {{ item.nextHealthCheckDateTime | date }}
        </span>
      </template>

      <template v-slot:[`item.healthCheck`]="{}">
        <v-chip
          v-if="
            nextPendingHealthCheck &&
              nextPendingHealthCheck.healthCheckScheduleId
          "
          small
          color="orange lighten-4"
          class="warning--text text--darken-1 text-uppercase mr-2 font-weight-regular"
        >
          {{ nextPendingHealthCheck.healthCheckScheduleStart | date }}
        </v-chip>

        <span
          v-if="
            nextPendingHealthCheck &&
              nextPendingHealthCheck.healthCheckTimeslotStartTime
          "
          class="mr-2"
        >
          {{
            new Date(nextPendingHealthCheck.healthCheckTimeslotStartTime)
              .toISOString()
              .slice(11, 16)
          }}
          - {{ nextPendingHealthCheck.healthCheckScheduleLocation }}
        </span>

        <v-chip
          v-if="
            nextPendingHealthCheck &&
              nextPendingHealthCheck.healthCheckConfirmedBy
          "
          color="green lighten-4"
          small
          class="success--text text--darken-1 text-uppercase mr-2"
        >
          {{ $t("riskAnalysis.status.confirmed") }}
        </v-chip>

        <template
          v-else-if="
            nextPendingHealthCheck &&
              canConfirmHealthCheck(nextPendingHealthCheck)
          "
        >
          <v-btn
            small
            color="primary"
            @click="confirmHealthCheck(nextPendingHealthCheck.healthCheckId)"
            class="mr-2"
          >
            {{ $t("riskAnalysis.confirmAnalysis.confirm") }}
          </v-btn>
          <v-btn
            small
            class="deep-orange lighten-4 error--text text--darken-3"
            @click="declineHealthCheck()"
          >
            {{ $t("healthCheck.decline.button") }}
          </v-btn>
        </template>
        <span
          v-if="
            nextPendingHealthCheck &&
              nextPendingHealthCheck.healthCheckIsSelfScheduling
          "
          >{{ $t("healthCheck.selfScheduling.assigned") }}</span
        >
        <span v-if="!nextPendingHealthCheck">-</span>
      </template>
    </v-data-table>

    <decline-health-check-dialog
      :show.sync="showDeclineHealthCheckDialog"
      :employee="employee"
      :item="nextPendingHealthCheck"
      @declined="$apollo.queries.nextPendingHealthCheck.refetch()"
    />

    <edit-results
      :show.sync="showEditResultsDialog"
      :employee="employee"
      :latestResult="employee.latestHealthCheckResult"
      @results-edited="$emit('refresh')"
    />
  </section>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import DeclineHealthCheckDialog from "@/components/healthCheck/DeclineHealthCheckDialog.vue";
import EditResults from "@/components/healthCheck/EditResults.vue";
import LatestHealthCheckResultQuery from "@/graphql/queries/healthCheck/NextPendingHealthCheck.gql";
import MyDepartmentTagIDsQuery from "@/graphql/queries/MyDepartmentTagIDs.gql";
import ConfirmHealthCheckMutation from "@/graphql/mutation/healthCheck/ConfirmHealthCheck.gql";

export default {
  props: {
    employee: {
      type: Object,
      required: true,
    },
  },
  components: {
    DeclineHealthCheckDialog,
    EditResults,
  },
  data() {
    return {
      showDeclineHealthCheckDialog: false,
      showEditResultsDialog: false,
    };
  },
  computed: {
    headers() {
      return [
        {
          text: this.$i18n.t("healthCheck.result.healthCheckDate"),
          value: "healthCheckOccurredAt",
          class: "text-uppercase subheading text-truncate",
          sortable: false,
        },
        {
          text: this.$i18n.t("healthCheck.result.decision"),
          value: "result",
          class: "text-uppercase subheading text-truncate",
          sortable: false,
        },
        {
          text: this.$i18n.t("healthCheck.result.suggestion"),
          value: "suggestion",
          class: "text-uppercase subheading text-truncate",
          sortable: false,
        },
        {
          text: this.$i18n.t("healthCheck.result.nextHealthCheckDate"),
          value: "nextHealthCheckDateTime",
          class: "text-uppercase subheading text-truncate",
          sortable: false,
        },
        {
          text: this.$i18n.t("healthCheck.nextAssigned"),
          value: "healthCheck",
          class: "text-uppercase subheading text-truncate",
          sortable: false,
        },
      ];
    },
    ...mapGetters(["role"]),
    ...mapState(["me"]),
  },
  methods: {
    canConfirmHealthCheck(healthCheck) {
      return (
        healthCheck.healthCheckScheduleStatus !== null &&
        !["waitingForResults", "confirmed", "pending"].includes(
          healthCheck.healthCheckScheduleStatus
        ) &&
        (this.isManager(this.employee, this.me) ||
          this.role("safetyRepresentative") ||
          this.employee.id === this.me.id ||
          (this.role("departmentManager") &&
            this.isDepartmentManager(this.employee)))
      );
    },
    isManager(employee, me) {
      return employee && employee.manager && me && employee.manager.id == me.id;
    },
    isDepartmentManager(employee) {
      const departmentTags = (employee.tags || []).filter(
        (t) => t.category === "department"
      );
      return departmentTags.some((tag) =>
        this.myDepartmentTagIDs.includes(tag.id)
      );
    },
    confirmHealthCheck(healthCheckId) {
      this.$apollo
        .mutate({
          mutation: ConfirmHealthCheckMutation,
          variables: {
            healthCheckId,
          },
        })
        .then(() => {
          this.$apollo.queries.nextPendingHealthCheck.refetch();
        })
        .catch((err) => {
          this.$store.commit("push_global_message", {
            color: "error",
            text: err,
          });
        });
    },
    declineHealthCheck() {
      this.showDeclineHealthCheckDialog = true;
    },
  },
  apollo: {
    nextPendingHealthCheck: {
      query: LatestHealthCheckResultQuery,
      variables() {
        return {
          employeeId: this.employee.id,
        };
      },
      skip() {
        return !this.employee || !this.employee.id;
      },
    },
    myDepartmentTagIDs: {
      query: MyDepartmentTagIDsQuery,
      skip() {
        return !this.role("departmentManager");
      },
    },
  },
};
</script>
